/** PROBATION-REVIEW-001 P3 — Reporting Manager's own probation reviews (any role; server-scoped). */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck } from 'lucide-react';
import ReviewDrawer from './ReviewDrawer';
import { StatusPill, REVIEW_STATUS, fmtDate } from './probationUi';
import { API, h, err } from './probationApi';
import './probation.css';

const OPEN = ['open', 'returned'];

export default function MyProbationReviews() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [active, setActive] = useState(null);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/hr/probation/reviews`, { headers: h(), params: { limit: 100 } }); setRows(r.data.data || []); }
    catch (e) { toast.error(err(e)); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const pending = (rows || []).filter(r => OPEN.includes(r.runtime_status || r.status));
  const rest = (rows || []).filter(r => !OPEN.includes(r.runtime_status || r.status));

  const Row = ({ r }) => {
    const st = r.runtime_status || r.status;
    const actionable = OPEN.includes(st);
    return (
      <li className="prb-tl-item" data-testid={`my-review-${r.id}`}>
        <span className={`prb-tl-dot ${(REVIEW_STATUS[st] || {}).cls || 'prb-slate'}`} />
        <div className="prb-tl-body">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-slate-800 truncate">{r.employee_name || r.employee_id} <span className="font-medium text-slate-400">· cycle {r.cycle_no} · due {fmtDate(r.due_date)}</span></p>
            <p className="text-[12px] text-slate-500 truncate">{r.team_at_schedule || '—'}{st === 'returned' && r.hr_decision?.note ? ` · HR: ${r.hr_decision.note}` : ''}{r.hr_decision?.decision === 'approved' ? ` · ${r.recommendation?.outcome === 'confirmed' ? 'Confirmed' : 'Extension approved'}` : ''}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <StatusPill map={REVIEW_STATUS} status={st} />
            {actionable && <button type="button" className="prb-btn-primary !h-8 !px-3 !text-[12.5px]" onClick={() => setActive(r)} data-testid={`my-review-open-${r.id}`}>{st === 'returned' ? 'Revise' : 'Review'}</button>}
          </div>
        </div>
      </li>
    );
  };

  return (
    <div className="min-h-screen bg-[#f6f7fb] px-4 py-6 sm:px-8" data-testid="my-probation-reviews">
      <div className="mx-auto max-w-3xl space-y-4">
        <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-500 hover:text-slate-800 transition-colors" data-testid="my-reviews-back"><ArrowLeft className="h-3.5 w-3.5" />Back</button>
        <section className="lv-card an-rise p-4">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center"><ClipboardCheck className="h-4.5 w-4.5" /></span>
            <div><h1 className="crm-section-title">Probation reviews</h1><p className="crm-section-sub mt-0.5">Employees who report to you · {pending.length} awaiting your review</p></div>
          </div>
          {rows === null ? <p className="mt-4 text-[13px] text-slate-500">Loading…</p>
            : rows.length === 0 ? <p className="mt-4 text-[13px] text-slate-500" data-testid="my-reviews-empty">No probation reviews are assigned to you.</p>
            : (
              <div className="mt-4 space-y-4">
                {pending.length > 0 && <ol className="prb-timeline" data-testid="my-reviews-pending">{pending.map(r => <Row key={r.id} r={r} />)}</ol>}
                {rest.length > 0 && <><p className="prb-metric-label">Scheduled & completed</p><ol className="prb-timeline" data-testid="my-reviews-other">{rest.map(r => <Row key={r.id} r={r} />)}</ol></>}
              </div>
            )}
        </section>
      </div>
      <ReviewDrawer open={!!active} onOpenChange={(o) => !o && setActive(null)} review={active} employee={active ? { full_name: active.employee_name, team: active.team_at_schedule } : null} probation={active?.probation} onSubmitted={load} />
    </div>
  );
}
